import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";
import { useOnboardingStore } from "../../store/onboardingSlice";
import { saveOnboardingStep } from "../../api/auth";
import { getPostAuthDestination } from "../../lib/billingSelection";
import { trackOnboardingComplete } from "../../lib/analytics";

const CHECKLIST_ITEMS = [
  "Business information saved",
  "Booking page is live",
  "Team notified (if applicable)",
  "Calendar connected (or can add later)",
];

export default function LaunchChecklistStep() {
  const navigate = useNavigate();
  const { setSaving, saving, reset } = useOnboardingStore();
  const [error, setError] = useState<string | null>(null);

  const handleFinish = async () => {
    setError(null);
    setSaving(true);
    try {
      await saveOnboardingStep({
        step: "launchChecklist",
        step_status: "complete",
        complete_wizard: true,
      });
      trackOnboardingComplete();
      reset();
      navigate(getPostAuthDestination(), { replace: true });
    } catch {
      setError("Failed to complete setup. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>You're almost live!</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review your setup before launching.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <List dense sx={{ mb: 3 }}>
        {CHECKLIST_ITEMS.map((item) => (
          <ListItem key={item} disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckCircleOutlineIcon color="success" />
            </ListItemIcon>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleFinish}
        disabled={saving}
      >
        {saving ? <CircularProgress size={22} color="inherit" /> : "Go to dashboard"}
      </Button>
    </Box>
  );
}
